import type {
  NoyauTrial,
  ReviewEventList,
  ReviewOperationsBody,
  ReviewOperationsResult,
  SessionReview,
} from '@matter_extractor/shared';
import {
  noyauTrialSchema,
  reviewEventListSchema,
  reviewOperationsResultSchema,
  sessionReviewSchema,
} from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The calls of the REVIEW page (relecture.md §4 to §11, issue #187): the
 * notebook of a session, its journal, the one write route, and the shortcut that
 * starts the analyst on the review.
 *
 * ## Why a file of its own
 *
 * `lib/session-api.ts` is edited by the lot of the existing Sessions screens in
 * the same cycle, and two lots in one module is a merge that goes wrong where
 * nobody looks. The request helper below is the one deliberate duplication, and
 * it keeps the one behaviour that matters: `details` falls back to the WHOLE
 * body, because a business refusal answers the contract body with no `details`
 * key at all - and the screens word a refusal from its `code`.
 *
 * Every answer is PARSED before it is returned.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { ...ADMIN_INIT, ...init });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

const reviewPath = (sessionId: string): string =>
  `/api/sessions/${encodeURIComponent(sessionId)}/review`;

/**
 * The key of the review, scoped by the signed-in operator like every
 * administration read. The page reads it again when it gets the focus back
 * (§4.4): a colleague may have written meanwhile.
 */
export const REVIEW_KEY = (email: string | null, sessionId: string): readonly unknown[] => [
  'session-review',
  email,
  sessionId,
];

/** The key of the journal, which every write invalidates. */
export const REVIEW_EVENTS_KEY = (email: string | null, sessionId: string): readonly unknown[] => [
  'session-review-events',
  email,
  sessionId,
];

/** `GET /api/sessions/{id}/review` - the review as it stands, readable in the trash too (§3.4). */
export function fetchSessionReview(sessionId: string): Promise<SessionReview> {
  return request(reviewPath(sessionId)).then((body) => sessionReviewSchema.parse(body));
}

/** `GET /api/sessions/{id}/review/events` - the journal, newest first (§10). */
export function fetchReviewEvents(sessionId: string): Promise<ReviewEventList> {
  return request(`${reviewPath(sessionId)}/events`).then((body) =>
    reviewEventListSchema.parse(body),
  );
}

/**
 * `POST /api/sessions/{id}/review/operations` - one or several gestures, each in
 * its own transaction with its journal line. A gesture of the screen sends
 * `via: null`; the answer is the review AFTER them and the lines that were
 * refused. A 409 is the refusal of the WHOLE request (`reviewRefusalOf`).
 */
export function postReviewOperations(
  sessionId: string,
  body: ReviewOperationsBody,
): Promise<ReviewOperationsResult> {
  return request(`${reviewPath(sessionId)}/operations`, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then((answer) => reviewOperationsResultSchema.parse(answer));
}

/**
 * `POST /api/sessions/{id}/noyau/trials` with the source « Relecture » (§11):
 * the shortcut « Tester l'analyste sur cette relecture ». It answers at once
 * with the trial pending, which the page then reads again until it settles
 * (`fetchNoyauTrial` of `session-api.ts`, under `NOYAU_TRIAL_KEY`). A refusal is
 * a 409 worded by `noyauTrialRefusalOf`.
 */
export function startReviewTrial(sessionId: string): Promise<NoyauTrial> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/noyau/trials`, {
    method: 'POST',
    body: JSON.stringify({ source: { kind: 'review' } }),
  }).then((body) => noyauTrialSchema.parse(body));
}
